import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router";
import { BloomLogo, Button } from "@bloom/ui";
import { LayoutDashboard, Film, Tag, Wallet, FileText, LogOut, Users, Search } from "lucide-react";
import { useAuth } from "../auth/auth-context.tsx";
import { AdminCmdK } from "../components/cmd-k.tsx";

const NAV = [
  { to: "/", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/series", label: "Series", Icon: Film },
  { to: "/users", label: "Users", Icon: Users },
  { to: "/pricing", label: "Pricing", Icon: Tag },
  { to: "/sales", label: "Sales", Icon: Wallet },
  { to: "/audit-log", label: "Audit log", Icon: FileText },
];

export function AdminShell({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [cmdkOpen, setCmdkOpen] = useState(false);

  // Global Cmd/Ctrl+K toggles the palette from anywhere in the admin app.
  // We swallow the default browser behaviour (some browsers map Ctrl+K to
  // the URL bar) so the admin never loses focus to the chrome.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdkOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex min-h-screen bg-[var(--brand-paper)] text-[var(--brand-ink)]">
      <aside className="flex w-56 shrink-0 flex-col gap-2 border-r border-[var(--brand-border)] bg-[var(--brand-ink-soft)] p-4 text-white">
        <Link to="/" className="px-2 py-2">
          <BloomLogo className="h-6 text-white" />
        </Link>
        <button
          type="button"
          onClick={() => setCmdkOpen(true)}
          className="mx-1 flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs text-white/70 transition-colors hover:bg-white/10"
        >
          <Search className="h-3.5 w-3.5" aria-hidden />
          <span>Search…</span>
          <kbd className="ml-auto rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px] text-white/80">
            ⌘K
          </kbd>
        </button>
        <nav className="mt-4 flex flex-col gap-1 text-sm">
          {NAV.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 rounded-lg px-3 py-2 transition-colors ${
                  isActive ? "bg-white/15 font-semibold" : "text-white/70 hover:bg-white/5"
                }`
              }
            >
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto text-xs text-white/60">
          <p className="px-2">{user?.email}</p>
          <p className="px-2 text-white/40">{user?.role}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={logout}
            className="mt-2 w-full justify-start text-white"
          >
            <LogOut className="h-4 w-4" aria-hidden /> Log out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-x-auto p-8">{children}</main>
      <AdminCmdK open={cmdkOpen} onOpenChange={setCmdkOpen} />
    </div>
  );
}
